import { getConfig } from '@superbet-group/web.lib.config';
import { ValidationError } from '@superbet-group/web.lib.utilities';
import {
    mapGetters,
    mapActions,
} from 'vuex';

import { logger } from '@app/logger';
import { clientStorageWithExpiry } from '@core/clientStorage/clientStorageWithExpiry';
import { UserService } from '@core/services/user/UserService';
import {
    sendVerifyIdentityEvent,
    AnalyticsKycEvent,
} from '@accountFederation/modules/idCardVerification/analytics/idVerificationAnalytics';
import { getIdVerificationStoreGetters } from '@accountFederation/modules/idCardVerification/analytics/idVerificationStoreGetters';
import { PhotoDeliveryMethods } from '@accountFederation/modules/idCardVerification/types';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { errorGuard } from '@utils/ErrorGuard';

// eslint-disable-next-line import/no-unused-modules
export default {
    props: {
        documentOnly: {
            type: Boolean,
            default: false,
        },
    },
    created() {
        (this as any).completeRegistrationUserDataKey
            = `user:${(this as any).user.userId}:completeRegistrationUserDataKey`;
        (this as any).completeRegistrationiSInvalidUserDataKey
            = `user:${(this as any).user.userId}:completeRegistrationiSInvalidUserDataKey`;

        if ((this as any).lastUploadedKyc && !(this as any).isMultiStepIdVerification) {
            (this as any).fields.file.value = (this as any).lastUploadedKyc;
        }
    },
    computed: {
        ...mapGetters('data/authentication', [
            'user',
        ]),
        ...mapGetters('ui/user/account', [
            'lastUploadedKyc',
        ]),
        ...mapGetters('ui/multiStepVerification', [
            'isMultiStepIdVerification',
        ]),
    },
    methods: {
        ...mapActions('ui/modal', [
            'setModal',
            'closeModal',
        ]),
        ...mapActions('ui/user/account', [
            'setLastUploadedKyc',
        ]),
        ...mapActions('data/authentication', [
            'setUserData',
        ]),
        loadDataFromClientStorage() {
            try {
                const clientStorageData = clientStorageWithExpiry.get<any>(
                    (this as any).completeRegistrationUserDataKey,
                );
                clientStorageData.bankAccountNumber
                    = getConfig().countrySpecific.auth.ibanPrefix + clientStorageData.bankAccountNumber;
                if ((this as any).user.isResident) {
                    clientStorageData.documentIssuingCountryId = getConfig().countrySpecific.auth.countryId;
                }
                return clientStorageData;
            } catch (err) {
                logger.error(err as Error);
                return {};
            }
        },
        onSubmit(this: any, documentOnly = false) {
            this.isLoading = true;
            this.error = '';

            this.setLastUploadedKyc(this.fields.file.value);
            const file = this.fields.file.value[0];
            const reader = new FileReader();
            reader.readAsDataURL(file);
            // eslint-disable-next-line sonarjs/cognitive-complexity
            reader.onload = async () => {
                await errorGuard(async () => {
                    if (documentOnly || this.documentOnly || this.user.phone) {
                        await UserService.getInstance()
                            .uploadIdCard(reader.result as string, file.name);
                    } else {
                        const data = this.loadDataFromClientStorage();
                        await UserService.getInstance()
                            .registrationSecondStep(
                                    reader.result as string,
                                    file.name,
                                    data,
                            );
                        sendVerifyIdentityEvent({
                            eventName: AnalyticsKycEvent.VerifyIdentityCompletedRegistration,
                            idVerificationStoreGetters: getIdVerificationStoreGetters(),
                        });
                        clientStorageWithExpiry.set(this.completeRegistrationiSInvalidUserDataKey, false);
                        this.setLastUploadedKyc(null);
                        this.setUserData({
                            key: 'phone',
                            value: data.phone,
                        });
                    }
                    sendVerifyIdentityEvent({
                        eventName: AnalyticsKycEvent.VerifyIdentityImageUpload,
                        type: this.documentTypeSelected,
                        method: PhotoDeliveryMethods.Upload,
                        idVerificationStoreGetters: getIdVerificationStoreGetters(),
                    });
                    this.$emit('success');
                }, {
                    onError: (e: any) => {
                        if (e instanceof ValidationError) {
                            if (e.fieldErrors.phone) {
                                if (this.user.phone) {
                                    // Hack solution for upload only Document
                                    this.onSubmit(true);
                                } else {
                                    this.previousStep(e as ValidationError);
                                }
                            } else if (e.fieldErrors.file) {
                                this.asyncErrors = e.fieldErrors;
                            } else {
                                clientStorageWithExpiry.set(this.completeRegistrationiSInvalidUserDataKey, true);
                                this.previousStep(e as ValidationError);
                            }
                        } else {
                            this.error = this.$t('Something went wrong. Please try again');
                            this.$emit('error', e);
                        }
                    },
                    showGlobalError: false,
                });
                this.isLoading = false;
            };
            reader.onerror = (e) => {
                this.isLoading = false;
                this.error = this.$t('Something went wrong. Please try again');
                logger.error((e as any));
            };
        },
        previousStep(errors: ValidationError) {
            (this as any).closeModal();
            (this as any).setModal({
                code: modalCodes.completeRegistration,
                data: errors,
            });
        },
    },
};
